import { CheckIcon } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";

import { ROLE_CONFIG } from "@/features/members/config";
import { ProjectRoleType } from "@/features/projects/types";
import { Role } from "@/features/members/types";
import { cn } from "@/shared/lib/utils/cn";

// ─── Shared skeleton ──────────────────────────────────────────────────────────

export function RoleSelectorSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-start gap-3 border border-border p-3">
          <Skeleton className="mt-0.5 size-4 shrink-0" />
          <div className="flex flex-col gap-1.5 flex-1">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-3/4" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Shared button ────────────────────────────────────────────────────────────

interface RoleButtonProps {
  roleName: string;
  description?: string;
  icon: React.ElementType;
  iconClassName: string;
  isSelected: boolean;
  disabled: boolean;
  onClick: () => void;
}

function RoleButton({ roleName, description, icon: Icon, iconClassName, isSelected, disabled, onClick }: RoleButtonProps) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} className={cn("flex items-start gap-3 border p-3 text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed", isSelected ? "border-primary bg-primary/5" : "border-border hover:bg-accent")}>
      <Icon className={cn("mt-0.5 size-4 shrink-0", isSelected ? "text-primary" : iconClassName)} />
      <div className="flex flex-col gap-0.5">
        <span className="text-xs font-semibold uppercase tracking-wider">{roleName}</span>
        {description && <span className="text-xs text-muted-foreground leading-relaxed">{description}</span>}
      </div>
      {isSelected && <CheckIcon className="ml-auto mt-0.5 size-4 shrink-0 text-primary" />}
    </button>
  );
}

// ─── Variant A: select by role name (used by ChangeRoleDialog) ────────────────

interface RoleSelectorProps {
  value: ProjectRoleType;
  onChange: (role: ProjectRoleType) => void;
  disabled?: boolean;
}

export function RoleSelector({ value, onChange, disabled = false }: RoleSelectorProps) {
  return (
    <div className="flex flex-col gap-3">
      {(Object.keys(ROLE_CONFIG) as ProjectRoleType[]).map((r) => {
        const cfg = ROLE_CONFIG[r];
        return <RoleButton key={r} roleName={r} description={cfg.description} icon={cfg.icon} iconClassName={cfg.className} isSelected={value === r} disabled={disabled} onClick={() => onChange(r)} />;
      })}
    </div>
  );
}

// ─── Variant B: select by role id (used by InviteMemberForm) ─────────────────

interface RoleByIdSelectorProps {
  roles: Role[];
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
}

export function RoleByIdSelector({ roles, value, onChange, disabled = false }: RoleByIdSelectorProps) {
  return (
    <div className="flex flex-col gap-3">
      {roles.map((role) => {
        const cfg = ROLE_CONFIG[role.name as ProjectRoleType];
        return (
          <RoleButton
            key={role.id}
            roleName={role.name}
            description={role.description ?? cfg?.description}
            icon={cfg?.icon ?? (() => null)}
            iconClassName={cfg?.className ?? "text-muted-foreground"}
            isSelected={value === role.id}
            disabled={disabled}
            onClick={() => onChange(role.id)}
          />
        );
      })}
    </div>
  );
}
